import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { PilotLeadStatus } from '@/models/pilot-crm.model';

export interface PipelineFunnelProps {
  data: Record<PilotLeadStatus, number>;
  onStageClick?: (status: PilotLeadStatus) => void;
}

const STAGE_COLORS: Record<string, string> = {
  new: '#3B82F6',
  invited: '#8B5CF6',
  replied: '#EC4899',
  call_booked: '#F59E0B',
  accepted: '#10B981',
  onboarding: '#06B6D4',
  live: '#22C55E',
  not_fit: '#EF4444',
  paused: '#6B7280',
  no_response: '#9CA3AF',
};

const STAGE_LABELS: Record<string, string> = {
  new: 'New',
  invited: 'Invited',
  replied: 'Replied',
  call_booked: 'Call Booked',
  accepted: 'Accepted',
  onboarding: 'Onboarding',
  live: 'Live',
  not_fit: 'Not Fit',
  paused: 'Paused',
  no_response: 'No Response',
};

const PIPELINE_ORDER: PilotLeadStatus[] = [
  'new',
  'invited',
  'replied',
  'call_booked',
  'accepted',
  'onboarding',
  'live',
];

const TERMINAL_ORDER: PilotLeadStatus[] = ['not_fit', 'paused', 'no_response'];

export const PipelineFunnel: React.FC<PipelineFunnelProps> = ({ data, onStageClick }) => {
  const pipelineData = PIPELINE_ORDER.map((status) => ({
    name: STAGE_LABELS[status],
    value: data[status] || 0,
    status,
    color: STAGE_COLORS[status],
  }));

  const terminalData = TERMINAL_ORDER.map((status) => ({
    name: STAGE_LABELS[status],
    value: data[status] || 0,
    status,
    color: STAGE_COLORS[status],
  }));

  const totalPipeline = pipelineData.reduce((sum, item) => sum + item.value, 0);
  const totalTerminal = terminalData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Pipeline Stages</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={pipelineData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: number) => [value, 'Leads']}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              />
              <Bar
                dataKey="value"
                radius={[0, 4, 4, 0]}
                cursor={onStageClick ? 'pointer' : 'default'}
                onClick={(data) => onStageClick && onStageClick(data.status)}
              >
                {pipelineData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-sm text-gray-500 mt-2">Total in pipeline: {totalPipeline}</p>
      </div>

      {totalTerminal > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Terminal States</h4>
          <div className="flex flex-wrap gap-4">
            {terminalData.map((item) => (
              <div
                key={item.status}
                className={`px-4 py-2 rounded-lg border ${
                  onStageClick ? 'cursor-pointer hover:bg-gray-50' : ''
                }`}
                style={{ borderColor: item.color }}
                onClick={() => onStageClick && onStageClick(item.status)}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm font-medium text-gray-700">{item.name}</span>
                </div>
                <p className="text-lg font-bold text-gray-900 mt-1">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t pt-4">
        <div className="flex flex-wrap gap-3">
          {[...pipelineData, ...terminalData].map((item) => (
            <div key={item.status} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-600">{item.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
